import type { Board } from "../board";
import { DamageType } from "../damageType";
import { BigExplosion } from "../effects/bigExplosion";
import { ParticleEffect } from "../effects/particleEffect";
import { ParticleType } from "../particleType";
import { PRIORITY_LAST, type ProcessableThing, type OnAddToBoard } from "../interfaces";
import { Mob } from "../mob";
import { FloatingRobot } from "../mobs/floatingRobot";
import { PhysicalThing } from "../physicalThing";
import { PhysicalThingWithHp, STD_BASE_HP } from "../physicalThingWithHp";
import { deserializeXY } from "../serialization";
import type { XY } from "../xy";
import { ZOrder } from "../zOrder";
import { ArtilleryRobot } from "./artilleryRobot";
import { MiningRobot } from "./miningRobot";
import { MultiTileThingPart } from "./multitile/multiTileThingPart";
import { Sound } from "./sounds/sound";
import { SoundId } from "./sounds/soundId";

export class Landmine extends PhysicalThing implements ProcessableThing, OnAddToBoard {
	static key = "Landmine";
	constructor(xy: XY) {
		super(xy, false);
	}

	getZOrder(): number {
		return ZOrder.ITEM_ON_FLOOR;
	}

	onAddToBoard(): void {
		this.checkAndExplode();
	}

	processTurn(_board: Board): void {
		this.checkAndExplode();
	}

	private checkAndExplode(): void {
		const board = this.board!;
		const things = board.getThingsAt(this.xy);
		const hasMob = things.some((t) => {
			if (!t.active) return false;
			if (t instanceof Mob) return !(t instanceof FloatingRobot);
			if (t instanceof MultiTileThingPart) {
				const core = t.controller.getThing();
				return core instanceof MiningRobot || core instanceof ArtilleryRobot;
			}
			return false;
		});

		if (!hasMob) return;

		// Explode: damage all in 3x3 area
		for (const areaXY of this.xy.getAreaInSquareRadius(1)) {
			if (!board.isInBounds(areaXY)) continue;
			for (const thing of board.getThingsAt(areaXY)) {
				if (thing instanceof PhysicalThingWithHp && thing.active) {
					thing.applyDamage(DamageType.BLAST, STD_BASE_HP, this);
				}
			}
		}
		board.addNonPhysicalThing(new Sound(SoundId.EXPLOSION, this.xy));
		board.addEffect(new BigExplosion(this.xy));
		board.addEffect(new ParticleEffect(this.xy, ParticleType.LARGE_GROUND_EXPLOSION, 30));
		board.removeAtEndOfTurn(this);
	}

	getProcessPriority(): number {
		return PRIORITY_LAST;
	}

	static deserialize(data: any): Landmine {
		const l = new Landmine(deserializeXY(data.xy));
		l.visualId = data.visualId;
		return l;
	}
}
